import { AddressCriteria } from '@src/main/modules/messages/domain/address/address-criteria.interface';
import { Address } from '@src/main/modules/messages/domain/address/address.interface';
import { AddressRepository } from '@src/main/modules/messages/domain/address/address.repository';
import { CreateAddressRequest } from '@src/main/modules/messages/infrastructure/dto/createAddressRequest';
import { UpdateAddressRequest } from '@src/main/modules/messages/infrastructure/dto/updateAddressRequest';
import {
  AddressEntity,
  toAddress,
} from '@src/main/modules/messages/infrastructure/entity/address.entity';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';

const path = '/addresses';

export const createAddressHttpRepository = (tokenProvider: TokenProvider): AddressRepository =>
  new AddressHttpRepository(tokenProvider);

class AddressHttpRepository implements AddressRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findByCriteria(criteria: AddressCriteria): Promise<PageResponse<Address>> {
    const token = await this.getAuthToken();
    const params = criteria;

    const response = await api.get<PageResponse<AddressEntity>>(path, token, params);

    return {
      ...response,
      items: response.items.map(toAddress),
    };
  }

  async findAll(): Promise<Address[]> {
    const token = await this.getAuthToken();
    const response = await api.get<AddressEntity[]>(`${path}/all`, token);
    return response.map(toAddress);
  }

  async findById(id: string): Promise<Address> {
    const token = await this.getAuthToken();
    const response = await api.get<AddressEntity>(`${path}/${id}`, token);
    return toAddress(response);
  }

  async create(address: CreateAddressRequest): Promise<Address> {
    const token = await this.getAuthToken();
    const response = await api.post<AddressEntity>(path, token, address);
    return toAddress(response);
  }

  async update(address: UpdateAddressRequest): Promise<Address> {
    const token = await this.getAuthToken();
    const response = await api.put<AddressEntity>(`${path}/${address.addressId}`, token, address);
    return toAddress(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${path}/${id}`, token);
  }
}
